import { useState, useEffect, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { employeeID } from "../Common/UIComponents";
import { APIURL } from "../Common/APIURL";

const hubBase = APIURL.replace(/\/api\/?$/i, "");
const HUB_URL = `${hubBase}/notificationHub?userId=${employeeID ?? ""}`;
const MAX_NOTIFICATIONS = 50;

export function useNotifications(onForceLogout) {
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const connectionRef = useRef(null);
  const onForceLogoutRef = useRef(onForceLogout);

  const addNotification = useCallback((payload) => {
    setNotifications((prev) => {
      const newItem = { ...payload, read: false };
      return [newItem, ...prev].slice(0, MAX_NOTIFICATIONS);
    });
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    await fetch(
      `${APIURL}LabManagement/MarkAllAsRead?employeeId=${employeeID}`,
      { method: "POST" },
    );
  }, []);

  const markRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    await fetch(`${APIURL}LabManagement/MarkAsRead?notificationId=${id}`, {
      method: "POST",
    });
  }, []);

  const clearAll = useCallback(() => setNotifications([]), []);

  useEffect(() => {
    onForceLogoutRef.current = onForceLogout;
  }, [onForceLogout]);

  // ── Single useEffect: load DB + start SignalR ──
  useEffect(() => {
    // 1. Load existing notifications from DB
    const loadFromDB = async () => {
      try {
        const res = await fetch(
          `${APIURL}LabManagement/GetNotifications?employeeId=${employeeID}`,
        );
        const data = await res.json();
        const mapped = data.map((n) => ({
          id: (n.NotificationId ?? n.notificationId)?.toString(),
          title: n.Title ?? n.title,
          message: n.Message ?? n.message,
          type: n.NotificationType ?? n.notificationType,
          timestamp: n.CreatedDate ?? n.createdDate,
          read: n.IsRead ?? n.isRead ?? false,
        }));
        setNotifications(mapped);
      } catch (err) {
        console.warn("Could not load notifications:", err);
      }
    };
    loadFromDB();

    // 2. Start ONE SignalR connection
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on("ReceiveNotification", (payload) => {
      console.log("Notification payload:", payload);
      addNotification(payload);
      const type = payload.type ?? payload.Type;
      if (type === "account_deactivated") {
        onForceLogoutRef.current?.();
      }
    });

    connection.on("ReceiveUpdate", () => {});

    connection.onreconnected(() => setConnected(true));
    connection.onclose(() => setConnected(false));

    connection
      .start()
      .then(() => setConnected(true))
      .catch((err) => console.warn("SignalR connect error:", err));

    // 3. Cleanup on unmount
    return () => {
      connection.stop();
    };
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    connected,
    markRead,
    markAllRead,
    clearAll,
  };
}
